import { useEffect, useState } from 'react';
import { effect } from '@preact/signals-react';
import qbSignal from 'QuickBooking/signal';
import { ANYONE, ANYONE_ID } from 'Calendar/anyone';
import StaffAvailablePicker from 'widgets/Staff/StaffAvailablePicker';
import TimeSlotAvailablePicker from 'widgets/Staff/TimeSlotAvailablePicker';
import aptSelectors from 'services/selectors/apt';
import aptActions from 'services/actions/apt';
import { IQuickBookingServiceItem } from 'services/types/quickBooking';
import { IStaffUI } from 'types/staff';
import { IDatePickerValue } from 'components/DatePicker';
import store from 'store';
import { useAppDispatch } from 'store/hooks';
import useCallerId from 'widgets/CallerId/useCallerId';
import { callerSelectResult } from 'Calendar/caller-booking-selection';
import { dateParamSignal } from 'CalendarFilter/DatePicker';
import storage from 'utils/sessionStorage';
import uuid from 'utils/uuid';
import moment from 'utils/moment';
import { ICallerCallItem } from '../types';

// Drives the inline "Booking — Order" composer through the existing qbSignal so it
// reuses getValues + the staff/time pickers + the quickBooking submit path.
// Seeds qbSignal directly on entry (NEVER qbSignal.open() — that pops the modal).
export const useBookingComposer = (item: ICallerCallItem) => {
  const dispatch = useAppDispatch();
  const callerSvc = useCallerId();
  const services = aptSelectors.quickBooking.services();
  const [staff, setStaff] = useState<IStaffUI>(ANYONE);

  useEffect(() => {
    qbSignal.currentDate.value = dateParamSignal.value?.clone() || moment();
    qbSignal.startTime.value = '';
    qbSignal.staffs.value = [ANYONE_ID];
    qbSignal.services.value = [];
    qbSignal.note.value = '';
    qbSignal.duration.value = 60;
    qbSignal.customer.value = { id: item.customerId ?? null, name: item.name, phone: item.phone };
    qbSignal.requestStaff.value = true; // caller bookings default to Request Staff on
    qbSignal.totalPeople.value = 1;
    qbSignal.callerId.value = item.id || ''; // caller_log id → sent in the quickBooking payload
    callerSelectResult.value = null; // drop any calendar range picked for a prior caller
    setStaff(ANYONE);
    if (!services.length) {
      const shopId = storage.shopId.get();
      if (shopId) dispatch(aptActions.quickBooking.getServices.fetch(shopId));
    }
  }, [item.id]); // re-seed whenever a different caller becomes active

  // Apply a range the user picked directly on the calendar → staff + time + duration.
  useEffect(() => {
    const dispose = effect(() => {
      const res = callerSelectResult.value;
      if (!res) return;
      qbSignal.staffs.value = [res.staffId];
      qbSignal.requestStaff.value = res.staffId !== ANYONE_ID;
      qbSignal.currentDate.value = res.start.clone();
      qbSignal.startTime.value = res.start.format('hh:mm A');
      qbSignal.duration.value = res.duration;
      const list = (store.getState().shop?.staffs || []) as IStaffUI[];
      setStaff(list.find((s) => s.id === res.staffId) || ANYONE);
    });
    return dispose;
  }, []);

  // Keep the composer date in sync with the calendar's selected date — changing the
  // day on the calendar reflects into the booking order.
  useEffect(() => {
    const dispose = effect(() => {
      const calendarDate = dateParamSignal.value;
      if (calendarDate) qbSignal.currentDate.value = calendarDate.clone();
    });
    return dispose;
  }, []);

  const pickStaff = async () => {
    const picked = await StaffAvailablePicker.get();
    if (!picked) return;
    qbSignal.staffs.value = [picked.id];
    qbSignal.requestStaff.value = picked.id !== ANYONE_ID;
    setStaff(picked);
  };

  const pickTime = async () => {
    const staffId = qbSignal.staffs.value[0] || ANYONE_ID;
    const slot = await TimeSlotAvailablePicker.get(staffId, qbSignal.currentDate.value);
    if (!slot) return;
    qbSignal.startTime.value = slot.format('hh:mm A');
    qbSignal.currentDate.value = slot;
  };

  const setDate = (date: IDatePickerValue) => {
    if (date) qbSignal.currentDate.value = date;
  };
  const setDuration = (value: number) => {
    qbSignal.duration.value = value;
  };
  const setNote = (value: string) => {
    qbSignal.note.value = value;
  };
  const addService = (svc: IQuickBookingServiceItem) => {
    qbSignal.services.value = [
      ...qbSignal.services.value,
      { label: svc.serviceName, value: uuid(), id: svc.id },
    ];
  };
  const removeService = (value: string) => {
    qbSignal.services.value = qbSignal.services.value.filter((o) => o.value !== value);
  };

  // Add the service if not already chosen, else remove it (by name) — for the picker modal.
  const toggleService = (svc: IQuickBookingServiceItem) => {
    const exists = qbSignal.services.value.some((o) => o.label === svc.serviceName);
    if (exists) {
      qbSignal.services.value = qbSignal.services.value.filter((o) => o.label !== svc.serviceName);
    } else {
      addService(svc);
    }
  };

  // Re Book: replace the composer services from a visit's comma-separated services
  // string (e.g. "Gel, M/P (Mani & Pedi)"). History items carry only a name, so we
  // search the catalog by name and auto-select the matching service (with its id);
  // matched items adopt the catalog name/id so they render + toggle consistently.
  // Unmatched names stay as free tags (id '' → getValues tolerates via `id || 0`).
  const prefillServices = (servicesStr: string) => {
    const names = servicesStr
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
    const norm = (s: string) => s.trim().toLowerCase();
    qbSignal.services.value = names.map((name) => {
      const key = norm(name);
      const match =
        services.find((s) => norm(s.serviceName) === key) ||
        services.find((s) => norm(s.serviceName).includes(key) || key.includes(norm(s.serviceName)));
      return { label: match?.serviceName ?? name, value: uuid(), id: match?.id ?? '' };
    });
  };

  const canBook = Boolean(qbSignal.startTime.value) && qbSignal.services.value.length > 0;

  const onSms = async () => {
    await callerSvc.sms(item.id, { id: item.customerId ?? null, name: item.name, phone: item.phone });
    // SMS handled this caller → refresh the pending list, close the panel if empty.
    dispatch(aptActions.getPendingCallers.fetch({ closeIfEmpty: true }));
  };

  const submit = () => {
    if (!canBook) return;
    const staffSelected = [
      {
        id: qbSignal.staffs.value[0] || ANYONE_ID,
        requestStaff: qbSignal.requestStaff.value,
        services: qbSignal.services.value,
      },
    ];
    dispatch(aptActions.quickBooking.doQuickBooking(qbSignal.getValues(staffSelected)));
  };

  return {
    services,
    staff,
    pickStaff,
    pickTime,
    setDate,
    setDuration,
    setNote,
    addService,
    removeService,
    toggleService,
    prefillServices,
    canBook,
    onSms,
    submit,
  };
};

export default useBookingComposer;
